import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
  useContext
} from 'react';
import useFetchWithCallbacks from '../../hooks/useFetchWithCallbacks';
import useTranslations from '../../hooks/useTranslations';
import { FetchCallbacksContext } from '../../internal-components/FetchCallbacks';
import { LOAD_FORM, UPDATE_FORM } from '../../store/modules/state/formBuilder';
import fetchAll from '../../utils/fetchAll';
import fetchAPI from '../../utils/fetchAPI';
import isPrivilegeEqualsTo from '../../utils/isPrivilegeEqualsTo';
import FormBuilderOverviewDialogView from '../view/FormBuilderOverviewDialogView';
import { FormBuilderContext } from './FormBuilderEngine';
import Cookies from 'universal-cookie';
import getTenant from '../../utils/getTenant';
import { useHistory } from 'react-router';
import useGlobalVariables from '../../hooks/useGlobalVariables';

const FormBuilderOverviewDialogEngine = ({
  isOpen,
  setIsOpen,
  isNew,
  startingFolder,
  setCurrentFolder,
  form,
  previousLocation
}) => {
  const { deleteJWT } = useGlobalVariables();
  const { openSnackbar } = useContext(FetchCallbacksContext);
  const { reduxDispatch, updateData } = useContext(FormBuilderContext);
  const t = useTranslations();
  const history = useHistory();
  const [folder, setFolder] = useState(startingFolder);
  const [folderOptions, setFolderOptions] = useState([]);
  const [title, setTitle] = useState(form.title);
  const [formKey, setFormKey] = useState(form.formKey);
  const [formKeyValidating, setFormKeyValidating] = useState(false);
  const [formKeyValidated, setFormKeyValidated] = useState(false);
  const [formKeyInvalid, setFormKeyInvalid] = useState(false);
  const [description, setDescription] = useState(form.instructions);
  const dialogRef = useRef();

  useEffect(() => {
    fetchAll('/forms/v1/folders').then(res => {
      let fetchedFolders = [];
      if (res && res.results && res.results.length) {
        fetchedFolders = res.results;
        fetchedFolders.sort((a, b) => {
          let fullnameA, fullnameB;
          fullnameA = a.name || '';
          fullnameB = b.name || '';
          return fullnameA.localeCompare(fullnameB);
        });
        setFolderOptions(fetchedFolders);
      }
    });
  }, []);

  useEffect(() => {
    setFormKey(form.formKey);
    setTitle(form.title);
    setDescription(form.instructions);
    setFolder(startingFolder);
  }, [form, startingFolder]);

  useEffect(() => {
    const trimmed = (formKey || '').trim().toUpperCase();
    const divRef = dialogRef.current;
    if (!isNew || formKey === undefined) {
      return;
    }
    const newFormKey = (formKey || '').trim().toUpperCase();
    if (newFormKey !== formKey) {
      setFormKey(newFormKey);
      return;
    }
    if (!trimmed.match(/^[A-Z0-9]{2,}$/)) {
      setFormKeyInvalid('invalid');
      setFormKeyValidated(true);
      divRef.classList.add('EUF-error');
      return;
    }
    setFormKeyValidating(true);
    fetchAPI(
      `/forms/v1/forms?query=[[{"field":"'formKey'","value": "${trimmed}","op":"="}]]`
    ).then(res => {
      if (res && res.results && res.results.length) {
        setFormKeyValidating(false);
        setFormKeyValidated(true);
        setFormKeyInvalid('unique');
        divRef.classList.add('EUF-error');
      } else {
        setFormKeyValidating(false);
        setFormKeyValidated(true);
        setFormKeyInvalid(false);
        divRef.classList.remove('EUF-error');
      }
    });
  }, [formKey]);

  const onFormKeyBlur = useCallback(() => {
    const newFormKey = (formKey || '').trim().toUpperCase();
    if (newFormKey !== formKey) {
      setFormKey(newFormKey);
    }
  }, [formKey]);

  const onDialogCancel = useCallback(() => {
    if (isNew) {
      history.push(previousLocation);
    }
    setIsOpen(false);
  }, [isNew, previousLocation]);

  const onDialogConfirm = useCallback(() => {
    const methodType = isNew ? 'POST' : 'PUT';
    const urlEndpoint = `/forms/v1/forms${isNew ? '' : `/${form.id}`}`;
    const fields = isNew ? [] : form.fields;
    const adminFields = isNew ? [] : form.adminFields;
    const body = {
      ...(isNew ? {} : form),
      formKey: formKey.trim(),
      title: title.trim(),
      instructions: description,
      sharingType: form.sharingType || 'internal',
      logo: false,
      fields,
      adminFields
    };

    if (form.customStatuses) {
      body.customStatuses = form.customStatuses;
    }

    if (form.automations) {
      body.automations = form.automations;
    }

    fetchAPI(urlEndpoint, {
      method: methodType,
      body,
      handleResponse: r =>
        r
          .text()
          .then(text => (text ? JSON.parse(text) : {}))
          .then(json => {
            if (r.ok) {
              return r;
            } else {
              return Promise.reject({
                response: r,
                json
              });
            }
          }),
      handleValidationError: data => {
        const error = data.errors ? data.errors[0] : {};
        const { code, message } = error;

        if (code === '23505') {
          openSnackbar('error', t('form-builder.overview.title_exists'));
        } else {
          openSnackbar('error', `Invalid: ${message}`);
        }
      }
    })
      .then(r => {
        const formId = isNew ? r.headers.map.location : form.id;

        if (folder && (folder !== startingFolder || isNew)) {
          fetchAPI(`/forms/v1/folders/${folder}/assign/${formId}`, {
            method: 'PUT'
          }).then(() => setCurrentFolder(folder));
        }
        // get ID from Location header
        openSnackbar(
          'success',
          isNew ? 'Your form has been created.' : 'Your form has updated.'
        );

        if (isNew) {
          // ======================= Patch start ========================
          // Details: https://vinciworksteam.atlassian.net/browse/O20-2651
          if (
            isPrivilegeEqualsTo('form:new') &&
            !isPrivilegeEqualsTo('form:create')
          ) {
            const cookies = new Cookies();
            const tenantId = getTenant();
            deleteJWT(tenantId);
            window.location.href = `/${tenantId}/form-builder/${formId}/edit`;
            return null;
          }
          // ======================= Patch end ========================
        }

        return formId;
      })
      .then(formId => {
        // UPDATE LOCAL STATE
        if (!formId) {
          return;
        }

        let getFormPromise =
          (form && {
            ...form,
            title: title.trim(),
            instructions: description
          }) ||
          {};
        let dispatchType = UPDATE_FORM;
        if (isNew) {
          // need fetch the data created by the server and load it to the state
          getFormPromise = new Promise((resolve, reject) =>
            fetchAPI(`/forms/v1/forms/${formId}`).then(resolve)
          );

          dispatchType = LOAD_FORM;
        }

        Promise.resolve(getFormPromise).then(form => {
          // legacy support
          reduxDispatch({
            type: dispatchType,
            payload: {
              id: formId,
              ...form
            }
          });
          isNew && history.push(`/form-builder/${formId}`);
          // updateData({
          //   type: 'set_form',
          //   payload: { ...form, title, instructions: description }
          // });
        });

        setIsOpen(false);
      });
  }, [isNew, title, formKey, description, form, folder]);

  const buttons = useMemo(() => {
    return [
      {
        text: 'dialog.cancel',
        variant: 'outlined',
        'data-testid': 'cancel-create-new-form'
      },
      {
        text: isNew
          ? 'form-builder.form_overview.btn.new.confirm'
          : 'form-builder.form_overview.btn.existing.confirm',
        disabled:
          !!!(title || '').trim() ||
          (isNew && (!formKeyValidated || formKeyInvalid)),
        onClick: 'confirm',
        'data-testid': isNew ? 'create-new-form' : 'save-form'
      }
    ];

    // not new form - use different text
  }, [
    isNew,
    onDialogCancel,
    onDialogConfirm,
    title,
    formKeyValidated,
    formKeyInvalid
  ]);

  const viewProps = {
    isNew,
    isOpen,
    setIsOpen,
    onDialogCancel,
    onDialogConfirm,
    buttons,
    onFormKeyBlur,
    formKey,
    title,
    setFormKey,
    setTitle,
    description,
    setDescription,
    folderOptions,
    folder,
    formKeyInvalid,
    formKeyValidated,
    formKeyValidating,
    setFolder
  };

  return <FormBuilderOverviewDialogView {...viewProps} ref={dialogRef} />;
};

export default FormBuilderOverviewDialogEngine;
